// src/types.ts
var BRIDGE_PROTOCOL_VERSION = 1;
var BRIDGE_PROTOCOL_MINOR_VERSION = 1;
var DEFAULT_TIMEOUT_MS = 3e4;

// src/bridge.ts
import { v4 as uuidv4 } from "uuid";

// src/schema.ts
var VALID_DOMAINS = [
  "nfc",
  "biometrics",
  "secureStorage",
  "camera",
  "crypto",
  "haptic",
  "analytics",
  "lifecycle",
  "documents",
  "navigation"
];
var VALID_TYPES = ["request", "response", "event"];
var ValidationError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "ValidationError";
  }
};
function isObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function assertString(obj, field) {
  if (typeof obj[field] !== "string") {
    throw new ValidationError(`Missing or invalid field: ${field} (expected string)`);
  }
}
function assertNumber(obj, field) {
  if (typeof obj[field] !== "number") {
    throw new ValidationError(`Missing or invalid field: ${field} (expected number)`);
  }
}
function isEvent(msg) {
  return msg.type === "event";
}
function validateRequest(obj) {
  assertString(obj, "method");
  if (!isObject(obj.params)) {
    throw new ValidationError("Request params must be an object");
  }
  return obj;
}
function validateResponse(obj) {
  assertString(obj, "requestId");
  if (typeof obj.success !== "boolean") {
    throw new ValidationError("Response success must be a boolean");
  }
  if (!obj.success && obj.error) {
    if (!isObject(obj.error)) {
      throw new ValidationError("Response error must be an object");
    }
    if (typeof obj.error.code !== "string" || typeof obj.error.message !== "string") {
      throw new ValidationError("Response error must have code and message strings");
    }
  }
  return obj;
}
function validateEvent(obj) {
  assertString(obj, "event");
  return obj;
}
function isResponse(msg) {
  return msg.type === "response";
}
function parseMessage(json) {
  let parsed;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new ValidationError("Invalid JSON");
  }
  if (!isObject(parsed)) {
    throw new ValidationError("Message must be an object");
  }
  const type = parsed.type;
  if (!VALID_TYPES.includes(type)) {
    throw new ValidationError(`Invalid message type: ${String(type)}`);
  }
  assertNumber(parsed, "version");
  if (parsed.version !== BRIDGE_PROTOCOL_VERSION) {
    throw new ValidationError(`Unsupported protocol version: ${parsed.version}`);
  }
  assertString(parsed, "id");
  assertNumber(parsed, "timestamp");
  const domain = parsed.domain;
  if (!VALID_DOMAINS.includes(domain)) {
    throw new ValidationError(`Invalid domain: ${String(domain)}`);
  }
  switch (type) {
    case "request":
      return validateRequest(parsed);
    case "response":
      return validateResponse(parsed);
    case "event":
      return validateEvent(parsed);
    default:
      throw new ValidationError(`Unknown message type: ${String(type)}`);
  }
}

// src/bridge.ts
var BrowserHostTransport = class {
  constructor(target, targetOrigin) {
    this.target = target;
    this.targetOrigin = targetOrigin;
    this.kind = "browser-host";
  }
  postMessage(json) {
    const request = parseOutgoingRequest(json);
    if (!request || request.domain !== "lifecycle") {
      return;
    }
    const message = mapLifecycleRequestToHostMessage(request);
    if (!message) {
      return;
    }
    this.target.postMessage(message, this.targetOrigin);
  }
};
var WebViewBridge = class {
  constructor(options = {}) {
    this.pending = /* @__PURE__ */ new Map();
    this.listeners = /* @__PURE__ */ new Map();
    this.destroyed = false;
    this.debug = options.debug ?? false;
    this.transport = options.transport ?? this.detectTransport(options.browserHost);
    globalThis.SelfNativeBridge = this;
    if (this.transport instanceof BrowserHostTransport) {
      this.hostMessageListener = this.createHostMessageListener(this.transport);
      window.addEventListener("message", this.hostMessageListener);
    }
  }
  detectTransport(browserHost) {
    if (globalThis.SelfNativeAndroid?.postMessage) {
      return globalThis.SelfNativeAndroid;
    }
    if (typeof window !== "undefined" && window.webkit?.messageHandlers?.SelfNativeIOS?.postMessage) {
      return window.webkit.messageHandlers.SelfNativeIOS;
    }
    if (typeof window !== "undefined" && window.ReactNativeWebView?.postMessage) {
      return window.ReactNativeWebView;
    }
    return this.detectBrowserHostTransport(browserHost);
  }
  detectBrowserHostTransport(browserHost) {
    if (typeof window === "undefined") {
      return null;
    }
    const hostTarget = window.parent !== window ? window.parent : window.opener && !window.opener.closed ? window.opener : null;
    if (!hostTarget) {
      return null;
    }
    if (!browserHost?.targetOrigin) {
      this.log("Browser host detected but no targetOrigin was configured; transport disabled");
      return null;
    }
    return new BrowserHostTransport(hostTarget, browserHost.targetOrigin);
  }
  log(...args) {
    if (this.debug) {
      console.log("[WebViewBridge]", ...args);
    }
  }
  send(message) {
    if (this.destroyed) {
      throw new Error("Bridge has been destroyed");
    }
    const json = JSON.stringify(message);
    this.log("\u2192", message.domain, message.method, message.params);
    if (!this.transport) {
      this.log("No native transport available, message dropped");
      return;
    }
    this.transport.postMessage(json);
  }
  createHostMessageListener(transport) {
    return (event) => {
      if (transport.targetOrigin !== "*" && event.origin !== transport.targetOrigin) {
        return;
      }
      if (event.source !== transport.target) {
        return;
      }
      const message = parseHostMessage(event.data);
      if (!message || message.type !== "self:cancel") {
        return;
      }
      this.dispatchEvent({
        type: "event",
        version: BRIDGE_PROTOCOL_VERSION,
        id: uuidv4(),
        domain: "lifecycle",
        event: "cancel",
        data: message.payload,
        timestamp: Date.now()
      });
    };
  }
  /**
   * Send a request and wait for a response.
   */
  request(domain, method, params = {}, timeoutMs = DEFAULT_TIMEOUT_MS) {
    if (this.destroyed) {
      return Promise.reject(new Error("Bridge has been destroyed"));
    }
    if (!this.transport) {
      return Promise.reject(new Error(`No transport available for bridge request: ${domain}.${method}`));
    }
    const id = uuidv4();
    const message = {
      type: "request",
      version: BRIDGE_PROTOCOL_VERSION,
      id,
      domain,
      method,
      params,
      timestamp: Date.now()
    };
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`Bridge request timed out: ${domain}.${method} (${timeoutMs}ms)`));
      }, timeoutMs);
      this.pending.set(id, {
        resolve,
        reject,
        timeout
      });
      this.send(message);
    });
  }
  /**
   * Fire-and-forget: send a request without waiting for a response.
   */
  fire(domain, method, params = {}) {
    const id = uuidv4();
    const message = {
      type: "request",
      version: BRIDGE_PROTOCOL_VERSION,
      id,
      domain,
      method,
      params,
      timestamp: Date.now()
    };
    this.send(message);
  }
  /**
   * Subscribe to native events for a domain:event pair.
   * Returns an unsubscribe function.
   */
  on(domain, event, handler) {
    const key = `${domain}:${event}`;
    if (!this.listeners.has(key)) {
      this.listeners.set(key, /* @__PURE__ */ new Set());
    }
    this.listeners.get(key).add(handler);
    return () => {
      const handlers = this.listeners.get(key);
      if (handlers) {
        handlers.delete(handler);
        if (handlers.size === 0) {
          this.listeners.delete(key);
        }
      }
    };
  }
  /**
   * Called by native via evaluateJavaScript:
   *   window.SelfNativeBridge._handleResponse(json)
   */
  _handleResponse(json) {
    this.log("\u2190 response", json.substring(0, 200));
    try {
      const msg = parseMessage(json);
      if (!isResponse(msg)) {
        this.log("Expected response, got:", msg.type);
        return;
      }
      this.resolveResponse(msg);
    } catch (err) {
      this.log("Failed to parse response:", err);
    }
  }
  /**
   * Called by native via evaluateJavaScript:
   *   window.SelfNativeBridge._handleEvent(json)
   */
  _handleEvent(json) {
    this.log("\u2190 event", json.substring(0, 200));
    try {
      const msg = parseMessage(json);
      if (!isEvent(msg)) {
        this.log("Expected event, got:", msg.type);
        return;
      }
      this.dispatchEvent(msg);
    } catch (err) {
      this.log("Failed to parse event:", err);
    }
  }
  resolveResponse(response) {
    const pending = this.pending.get(response.requestId);
    if (!pending) {
      this.log("No pending request for:", response.requestId);
      return;
    }
    clearTimeout(pending.timeout);
    this.pending.delete(response.requestId);
    if (response.success) {
      pending.resolve(response.data);
    } else {
      const error = response.error ?? {
        code: "UNKNOWN",
        message: "Unknown error"
      };
      const err = new Error(error.message);
      err.code = error.code;
      if (error.details) {
        err.details = error.details;
      }
      pending.reject(err);
    }
  }
  dispatchEvent(event) {
    const key = `${event.domain}:${event.event}`;
    const handlers = this.listeners.get(key);
    if (handlers) {
      for (const handler of handlers) {
        try {
          handler(event.data);
        } catch (err) {
          this.log("Event handler error:", err);
        }
      }
    }
  }
  /**
   * Check if a native transport is available.
   */
  get isConnected() {
    return this.transport !== null;
  }
  /**
   * True when lifecycle traffic is being proxied to a browser host via postMessage.
   */
  get usesBrowserHostTransport() {
    return this.transport?.kind === "browser-host";
  }
  /**
   * Number of pending requests.
   */
  get pendingCount() {
    return this.pending.size;
  }
  /**
   * Tear down the bridge: reject all pending, clear listeners, remove global.
   */
  destroy() {
    this.destroyed = true;
    if (this.hostMessageListener && typeof window !== "undefined") {
      window.removeEventListener("message", this.hostMessageListener);
    }
    for (const [id, pending] of this.pending) {
      clearTimeout(pending.timeout);
      pending.reject(new Error("Bridge destroyed"));
      this.pending.delete(id);
    }
    this.listeners.clear();
    if (globalThis.SelfNativeBridge === this) {
      globalThis.SelfNativeBridge = void 0;
    }
  }
};
function parseOutgoingRequest(json) {
  try {
    const candidate = JSON.parse(json);
    if (candidate.type !== "request" || candidate.version !== BRIDGE_PROTOCOL_VERSION || typeof candidate.id !== "string" || typeof candidate.domain !== "string" || typeof candidate.method !== "string" || typeof candidate.timestamp !== "number" || typeof candidate.params !== "object" || candidate.params === null) {
      return null;
    }
    return candidate;
  } catch {
    return null;
  }
}
function mapLifecycleRequestToHostMessage(request) {
  switch (request.method) {
    case "ready":
      return {
        type: "self:ready",
        version: BRIDGE_PROTOCOL_VERSION,
        payload: request.params
      };
    case "setResult":
      return {
        type: "self:result",
        version: BRIDGE_PROTOCOL_VERSION,
        payload: request.params
      };
    case "dismiss":
      return {
        type: "self:dismiss",
        version: BRIDGE_PROTOCOL_VERSION,
        payload: request.params
      };
    default:
      return null;
  }
}
function parseHostMessage(data) {
  let candidate = data;
  if (typeof candidate === "string") {
    try {
      candidate = JSON.parse(candidate);
    } catch {
      return null;
    }
  }
  if (typeof candidate !== "object" || candidate === null) {
    return null;
  }
  const message = candidate;
  if (message.version !== BRIDGE_PROTOCOL_VERSION || message.type !== "self:ready" && message.type !== "self:result" && message.type !== "self:dismiss" && message.type !== "self:cancel") {
    return null;
  }
  return {
    type: message.type,
    version: BRIDGE_PROTOCOL_VERSION,
    payload: typeof message.payload === "object" && message.payload !== null ? message.payload : {}
  };
}
export {
  BRIDGE_PROTOCOL_MINOR_VERSION,
  BRIDGE_PROTOCOL_VERSION,
  DEFAULT_TIMEOUT_MS,
  WebViewBridge
};
//# sourceMappingURL=index.js.map